import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "next-mdx-import-source-file";
export const meta = {
  title: "NC Climate Risk & Home Insurance Impact",
  description: "Climate risks drive NC home insurance premiums above $6,000 in coastal counties. Find out where rates are rising fastest and what's coming in 2026.",
  category: "General",
  tags: [],
  featuredImage: {
    src: "/images/blog/AdobeStock_445654541-scaled.webp",
    alt: "NC Counties Where Climate is Making the Biggest Impact on Home Insurance"
  },
  author: {
    name: "Anja Solum, Kara Credle",
    role: "Author",
    image: ""
  },
  publishedAt: "July 29, 2025",
  updatedAt: "August 13, 2025",
  showTOC: true
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    em: "em",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    hr: "hr",
    li: "li",
    p: "p",
    strong: "strong",
    table: "table",
    tbody: "tbody",
    td: "td",
    th: "th",
    thead: "thead",
    tr: "tr",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.h2, {
      children: meta.title
    }), "\n", meta.description, "\n", _jsx(_components.hr, {}), "\n", _jsxs(_components.p, {
      children: ["Home insurance rates are climbing across the country. Nationally, premiums increased by more than 10% in both 2023 and 2024, according to S&P Global Market Intelligence. A ", _jsx(_components.a, {
        href: "https://home.treasury.gov/news/press-releases/jy2791",
        children: "U.S. Treasury report"
      }), " found that homeowners in high climate-risk zones pay 82% more in premiums than those in low-risk areas. "]
    }), "\n", _jsxs(_components.p, {
      children: ["North Carolina is feeling that pressure. The state now averages nearly three times more billion-dollar disasters than its annual average of 2.7 events between 1980 and 2024, according to the ", _jsx(_components.a, {
        href: "https://web.archive.org/web/20250123065356/https://www.ncei.noaa.gov/access/billions/state-summary/NC",
        children: "National Oceanic and Atmospheric Administration"
      }), " (NOAA). And while coastal counties shoulder major climate risks, our analysis shows the home insurance burden isn’t limited to the shoreline."]
    }), "\n", _jsx(_components.p, {
      children: "To uncover where a home insurance crisis could be brewing in the Tar Heel State, the research team at Guardian ranked 93 North Carolina counties using data from Federal Emergency Management Agency (FEMA), the U.S. Treasury, Federal Financial Institutions Examination Council (FFIEC), Quadrant Information Services, and the North Carolina Department of Insurance. The analysis examined climate risk, insurance costs, historical trends, and homeowner strain. Seven counties did not have available data."
    }), "\n", _jsx(_components.h3, {
      children: "How We Measured Risk & Resiliency +"
    }), "\n", _jsx(_components.p, {
      children: "To determine which NC counties experience the most and least pressure from both climate and insurance-related factors, we looked at five key metrics:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Climate Risk Score:"
        }), " Higher values indicate more exposure to natural hazards and disasters like hurricanes, flooding, wind, and wildfire."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Paid Loss Ratio:"
        }), " The percentage of premiums that insurers pay out in claims. Higher values may point to more property damage or risk and can drive up costs."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Nonrenewal Rate:"
        }), " The percentage of policies insurers choose not to renew. Higher rates may signal that insurers are pulling out due to risk or unprofitability, leaving homeowners with less options."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Claim Frequency:"
        }), " The percentage of claims insurers have paid relative to the number of policies they’ve issued. Higher frequency suggests more frequent weather or disaster events in the area."]
      }), "\n", _jsxs(_components.li, {
        children: [_jsx(_components.strong, {
          children: "Claim Severity:"
        }), " The average cost of each insurance claim. Higher severity means costlier damage, which can cause higher insurance premiums or stricter coverage."]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: _jsxs(_components.em, {
        children: [_jsx(_components.strong, {
          children: _jsx(_components.a, {
            href: "/blog/about",
            children: "About Guardian Service"
          })
        }), ": We’re an independent insurance agency based in Raleigh, North Carolina, reimagining the insurance experience for homeowners. Our agents aren’t commissioned (seriously — zero commission), and our entire model is based on getting the right coverage for each person by continually re-shopping across 50+ carriers. It’s insurance designed to adapt to homeowners, not the other way around."]
      })
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "Key Takeaways"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "New Hanover County, home to Wilmington, ranks highest for climate and insurance vulnerability. Contributing factors include a 36% paid loss ratio and claims averaging more than $17,000 each."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "North Carolina’s two most populous counties, Mecklenburg and Wake, both rank among the five most impacted counties with nonrenewal rates above 3%, frequent claims, and premiums above $3,000."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "In 16 counties, home insurance premiums already exceed $4,000, with New Hanover, Carteret, and Dare all topping $6,000 as of the first quarter in 2025."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "Claim frequency has surged, with Moore County seeing claims on 16% of policies, the highest in the state and well above the national average (5%)."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "By 2026, New Hanover is projected to see the steepest premium jump at more than $1,400, bringing the average annual cost close to $8,000."
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h3, {
      children: "Where Climate and Insurance Pressure Hit Hardest"
    }), "\n", _jsx(_components.p, {
      children: "New Hanover County, along the coast and home to Wilmington, ranks as the county with the most climate and insurance pressure. Among the 15 most burdened counties, one-third are coastal, including Brunswick, Onslow, Dare, and Pender. The other 10 are inland."
    }), "\n", _jsx(_components.p, {
      children: "Cumberland County, which is inland, follows New Hanover, driven by the third-highest claim frequency in the state and a nonrenewal rate that ranks seventh-highest. Several severe weather events that occurred in 2022, including damaging windstorms in the spring and the remnants of Hurricane Ian in the fall, contributed to elevated claim activity."
    }), "\n", _jsx(_components.p, {
      children: "Two other counties of note in the top five include Wake and Mecklenburg, where nonrenewal rates are above 3% and claim frequency exceeds 6%. Annual insurance premiums are around the national average ($3,448) in both counties, with Mecklenburg roughly $300 lower in the first quarter of 2025."
    }), "\n", _jsx(_components.h3, {
      children: "What the Insurance Landscape Looks Like in North Carolina Today"
    }), "\n", _jsx(_components.p, {
      children: "In 16 counties, average premiums exceed $4,000. New Hanover has the highest with an average of $6,631, followed by Carteret and Dare, both also above $6,000. Claim severity is highest in Chowan and Brunswick counties, where average payouts are more than $20,000. At the other end of the list, Moore is the only county with an average claim cost below $5,000."
    }), "\n", _jsx(_components.p, {
      children: "Despite having a low claim severity, Moore’s claim frequency is the highest in the state, above 10% along with Hoke County. Washington and Greene are the only counties where nonrenewal rates exceed 5%, which is well above the national average of 1%. Debt-to-income ratios, which suggest that homeowners in an area may have less of an ability to absorb rising insurance costs or unexpected repairs, are highest in Scotland and Polk counties, where nearly half of household income is allocated toward debt payments (46% and 45%, respectively)."
    }), "\n", _jsx(_components.h4, {
      children: "What Else to Know"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["Buncombe County ranks No. 61 overall, which does not yet reflect the extensive damage from Hurricane Helene in 2024, which caused an ", _jsx(_components.a, {
          href: "https://weather.com/storms/hurricane/news/2025-03-19-hurricane-helene-final-report-nhc-deaths-damage-flooding",
          children: "estimated $78.7 billion in damages"
        }), "."]
      }), "\n", _jsxs(_components.li, {
        children: ["In July 2025, Tropical Storm Chantal brought up to a foot of rain to parts of the Triangle, triggering severe flooding and damage totals reaching into the millions of dollars in 13 counties, including Durham, Orange, and Wake. The state governor declared a ", _jsx(_components.a, {
          href: "https://www.wral.com/news/local/state-of-emergency-chantal-north-carolina-july-2025/",
          children: "state of emergency"
        }), " on July 17."]
      }), "\n"]
    }), "\n", _jsx(_components.h3, {
      children: "How We Got Here"
    }), "\n", _jsx(_components.p, {
      children: "The last five years have brought sharp increases in both the cost and frequency of insurance claims across North Carolina. Claim severity has grown by 33% statewide."
    }), "\n", _jsx(_components.p, {
      children: "In Chowan County, average claim costs increased more than six times between 2018 and 2022, reaching $28,206. Onslow and Carteret counties saw increases of over three times more. Premiums are up as well. While 48 counties held close to the 8% state average, others, such as Dare and Brunswick, saw policy costs rise by more than 20%."
    }), "\n", _jsx(_components.h4, {
      children: "Top 10 Counties by Claim Severity Change (2018-2022)"
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Rank"
          }), _jsx(_components.th, {
            children: "County"
          }), _jsx(_components.th, {
            children: "2018 Claim Severity"
          }), _jsx(_components.th, {
            children: "2022 Claim Severity"
          }), _jsx(_components.th, {
            children: "% Change"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "1"
          }), _jsx(_components.td, {
            children: "Chowan County"
          }), _jsx(_components.td, {
            children: "$4,556"
          }), _jsx(_components.td, {
            children: "$28,206"
          }), _jsx(_components.td, {
            children: "519.2%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2"
          }), _jsx(_components.td, {
            children: "Onslow County"
          }), _jsx(_components.td, {
            children: "$5,092"
          }), _jsx(_components.td, {
            children: "$18,289"
          }), _jsx(_components.td, {
            children: "259.2%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "3"
          }), _jsx(_components.td, {
            children: "Carteret County"
          }), _jsx(_components.td, {
            children: "$3,702"
          }), _jsx(_components.td, {
            children: "$11,191"
          }), _jsx(_components.td, {
            children: "202.3%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "4"
          }), _jsx(_components.td, {
            children: "Brunswick County"
          }), _jsx(_components.td, {
            children: "$7,800"
          }), _jsx(_components.td, {
            children: "$20,471"
          }), _jsx(_components.td, {
            children: "162.4%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "Beaufort County"
          }), _jsx(_components.td, {
            children: "$4,351"
          }), _jsx(_components.td, {
            children: "$10,074"
          }), _jsx(_components.td, {
            children: "131.5%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "6"
          }), _jsx(_components.td, {
            children: "Pender County"
          }), _jsx(_components.td, {
            children: "$7,468"
          }), _jsx(_components.td, {
            children: "$17,215"
          }), _jsx(_components.td, {
            children: "130.5%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "7"
          }), _jsx(_components.td, {
            children: "New Hanover County"
          }), _jsx(_components.td, {
            children: "$7,554"
          }), _jsx(_components.td, {
            children: "$17,386"
          }), _jsx(_components.td, {
            children: "130.2%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "8"
          }), _jsx(_components.td, {
            children: "Nash County"
          }), _jsx(_components.td, {
            children: "$7,796"
          }), _jsx(_components.td, {
            children: "$13,804"
          }), _jsx(_components.td, {
            children: "77.1%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "9"
          }), _jsx(_components.td, {
            children: "Cumberland County"
          }), _jsx(_components.td, {
            children: "$6,561"
          }), _jsx(_components.td, {
            children: "$11,372"
          }), _jsx(_components.td, {
            children: "73.3%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "10"
          }), _jsx(_components.td, {
            children: "Scotland County"
          }), _jsx(_components.td, {
            children: "$7,799"
          }), _jsx(_components.td, {
            children: "$13,435"
          }), _jsx(_components.td, {
            children: "72.3%"
          })]
        })]
      })]
    }), "\n", _jsx(_components.h4, {
      children: "Bottom 10 Counties by Claim Severity Change (2018-2022)"
    }), "\n", _jsxs(_components.table, {
      children: [_jsx(_components.thead, {
        children: _jsxs(_components.tr, {
          children: [_jsx(_components.th, {
            children: "Rank"
          }), _jsx(_components.th, {
            children: "County"
          }), _jsx(_components.th, {
            children: "2018 Claim Severity"
          }), _jsx(_components.th, {
            children: "2022 Claim Severity"
          }), _jsx(_components.th, {
            children: "% Change"
          })]
        })
      }), _jsxs(_components.tbody, {
        children: [_jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "1"
          }), _jsx(_components.td, {
            children: "Camden County"
          }), _jsx(_components.td, {
            children: "$12,051"
          }), _jsx(_components.td, {
            children: "$9,549"
          }), _jsx(_components.td, {
            children: "-20.8%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "2"
          }), _jsx(_components.td, {
            children: "Columbus County"
          }), _jsx(_components.td, {
            children: "$11,716"
          }), _jsx(_components.td, {
            children: "$9,089"
          }), _jsx(_components.td, {
            children: "-22.4%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "3"
          }), _jsx(_components.td, {
            children: "Madison County"
          }), _jsx(_components.td, {
            children: "$14,246"
          }), _jsx(_components.td, {
            children: "$10,800"
          }), _jsx(_components.td, {
            children: "-24.2%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "4"
          }), _jsx(_components.td, {
            children: "Pasquotank County"
          }), _jsx(_components.td, {
            children: "$15,124"
          }), _jsx(_components.td, {
            children: "$10,108"
          }), _jsx(_components.td, {
            children: "-33.2%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "5"
          }), _jsx(_components.td, {
            children: "Transylvania County"
          }), _jsx(_components.td, {
            children: "$9,762"
          }), _jsx(_components.td, {
            children: "$6,476"
          }), _jsx(_components.td, {
            children: "-33.7%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "6"
          }), _jsx(_components.td, {
            children: "Swain County"
          }), _jsx(_components.td, {
            children: "$19,989"
          }), _jsx(_components.td, {
            children: "$13,161"
          }), _jsx(_components.td, {
            children: "-34.2%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "7"
          }), _jsx(_components.td, {
            children: "Currituck County"
          }), _jsx(_components.td, {
            children: "$14,038"
          }), _jsx(_components.td, {
            children: "$8,910"
          }), _jsx(_components.td, {
            children: "-36.5%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "8"
          }), _jsx(_components.td, {
            children: "Hertford County"
          }), _jsx(_components.td, {
            children: "$12,448"
          }), _jsx(_components.td, {
            children: "$7,504"
          }), _jsx(_components.td, {
            children: "-39.7%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "9"
          }), _jsx(_components.td, {
            children: "Cherokee County"
          }), _jsx(_components.td, {
            children: "$17,748"
          }), _jsx(_components.td, {
            children: "$9,382"
          }), _jsx(_components.td, {
            children: "-47.1%"
          })]
        }), _jsxs(_components.tr, {
          children: [_jsx(_components.td, {
            children: "10"
          }), _jsx(_components.td, {
            children: "Clay County"
          }), _jsx(_components.td, {
            children: "$23,420"
          }), _jsx(_components.td, {
            children: "$12,311"
          }), _jsx(_components.td, {
            children: "-47.4%"
          })]
        })]
      })]
    }), "\n", _jsx(_components.h3, {
      children: "What's Coming Next for Homeowners: Rate Hikes for 2026"
    }), "\n", _jsxs(_components.p, {
      children: ["Based on ", _jsx(_components.a, {
        href: "https://apnews.com/article/north-carolina-insurance-homes-proposal-commissioner-3b4263c2cbb03b096c8e52946df759d3",
        children: "approved increases"
      }), " to current rate filings, many counties are on track to see significant insurance premium hikes by June 2026."]
    }), "\n", _jsx(_components.p, {
      children: "New Hanover is projected to see the steepest increase, with premiums expected to rise by more than $1,400, pushing the average annual cost close to $8,000. Other coastal counties such as Carteret, Onslow, and Brunswick are also projected to see some of the biggest increases, ranging from $1,051 to $1,377. Inland counties, such as Duplin and Lenoir, are expected to see annual increases of $1,326 by 2026."
    }), "\n", _jsxs(_components.p, {
      children: ["At the other end of the spectrum, Haywood, Swain, Madison, and Transylvania counties are all expected to see no increase in their property tax rates. These ", _jsx(_components.a, {
        href: "https://www.newsobserver.com/news/business/article284060998.html",
        children: "western mountain counties"
      }), " have lower overall risk exposure due to their location and saw a reduction to zero from the original 4% proposed rate."]
    }), "\n", _jsx(_components.h4, {
      children: "Prepare for 2026 rate changes"
    }), "\n", _jsx(_components.p, {
      children: "With projected increases varying widely by county, now is the time to optimize your coverage. Let our Zero-Commission Agents find you the best protection at the best price."
    }), "\n", _jsx(_components.h3, {
      children: "Methodology"
    }), "\n", _jsx(_components.p, {
      children: "To evaluate how climate risk and insurance burdens vary across North Carolina, Guardian Service analyzed county-level data from a mix of federal, state, and proprietary sources. The analysis includes 93 North Carolina counties and focuses on current insurance market strain, climate exposure, historical trends, and homeowner financial resilience. Seven counties were excluded due to unavailable data."
    }), "\n", _jsx(_components.p, {
      children: "Data sources include:"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "FEMA’s National Risk Index (NRI) for county-level climate risk scores"
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "U.S. Treasury’s Analyses of U.S. Homeowners Insurance Markets, 2018–2022: Climate-Related Risks and Other Factors for historical paid loss ratios, claim frequency, claim severity, nonrenewal rates, and historical premium rates"
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "Quadrant Information Services for average home insurance premiums as of Q1 2025. Averages are based on an insured homeowner profile of $350,000 in coverage for the dwelling, $35,000 for other structures, $210,000 for personal property, $70,000 for loss of use, $300,000 for liability, and $1,000 for medical payments."
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsxs(_components.p, {
          children: ["North Carolina Department of Insurance for projected premium increases from the ", _jsx(_components.a, {
            href: "https://www.ncdoi.gov/2024-proposed-homeowners-insurance-increase-settlement-table/",
            children: "2024 Proposed Homeowners Insurance Increase Settlement"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "Home Mortgage Disclosure Act (HMDA) for borrower debt-to-income ratios, published by the Federal Financial Institutions Examination Council (FFIEC)"
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "U.S. Census Bureau’s 5-Year American Community Survey (ACS) for population context and geographic comparisons"
        }), "\n"]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "We ranked counties across multiple metrics to identify where insurance-related pressures are greatest. Higher rankings indicate greater overall strain. All data is based on the most recent releases available as of July 2025."
    }), "\n", _jsx(_components.h4, {
      children: "Fair Use Policy"
    }), "\n", _jsx(_components.p, {
      children: "We encourage journalists and reporters to share our findings on North Carolina county climate risk and the respective insurance impact. If you choose to do so, please link back to our original story to give us proper credit for our research."
    }), "\n", _jsxs(_components.p, {
      children: ["For questions or expert insight into our research: ", _jsx(_components.a, {
        href: "mailto:dayna.edens@guardianservice.com",
        children: "dayna.edens@guardianservice.com"
      })]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
